
import RichText from '@/views/Project/Form/components/richText.vue'
import Editor from '@/components/Editor/Editor.vue'
import ProjectMixin from '@/_mixins/project'
import { Component, Prop } from 'vue-property-decorator'
import { patchRequest, postRequest } from '@/plugins/api-axios'
import { Mixins } from 'vue-mixin-decorator'
import FormCheckboxGroup from '@/views/Project/Form/components/formCheckboxGroup.vue'
import { serialise } from 'kitsu-core'
import RichTextField from '@/components/RichTextField/richTextField.vue'
import EditorViewer from '@/_components/editorJS/viewer/viewer.vue'
import DialogTemplate from '@/_components/dialog/template/template.vue'
import type MixinInterface from '@/_mixins'
import { Step } from '../viewModels/step'
import { Input } from '../viewModels/input'
import { isEqualWith } from 'lodash'
import { DocumentInput } from '../viewModels/documentInput'
import { Debounce } from 'lodash-decorators'

type StepInputState = 'Added' | 'Updated' | ''

@Component({
    mixins: [ProjectMixin],
    components: {
        DialogTemplate,
        FormCheckboxGroup,
        Editor,
        EditorViewer,
        RichText,
        RichTextField,
    },
})
export default class FormResponse extends Mixins<MixinInterface>(ProjectMixin) {
    @Prop({ default: () => ({}) }) formResponse!: any
    @Prop({ default: () => ({}) }) latestResponse!: any
    @Prop({ default: () => ({}) }) revision!: any
    @Prop() language!: string
    @Prop({ type: Boolean }) isPreview!: boolean

    isSubmitting = false
    isUploadingFile = false
    isResponseAvailable = Boolean(
        this.formResponse?.inputData && Object.keys(this.formResponse.inputData).length
    )
    isLatestResponseAvailable = Boolean(
        this.latestResponse?.inputData && Object.keys(this.latestResponse.inputData).length
    )
    currentStepIndex = 1
    revisionSteps: Step[] = []
    stepQuiz = {}
    stepInputChanges: Record<string, StepInputState> = {}
    visibleInputs = {}
    inputDependencies = {}
    inputData = this.formResponse.inputData || {}
    inputItemHelpText = null as Maybe<string>
    inputHelpTextDialogOpen = false
    domObserver?: MutationObserver = undefined

    get revisionData() {
        return this.formResponse?.revision?.data || this.revision
    }

    get isLastStep() {
        return this.currentStepIndex === this.revisionSteps.length
    }

    get rules() {
        return {
            required: (v: string | string[]) => {
                if (Array.isArray(v)) {
                    return !!v.length || this.$t('fe_validation.error_required')
                }

                return !!v || this.$t('fe_validation.error_required')
            },
        }
    }

    get allowedFileTypes() {
        let fileMetadata = this.$store.getters.attachmentConstraints.maxBytes
            ? this.$store.getters.attachmentConstraints
            : this.getFileRequirements()
        return fileMetadata?.allowedExtensions
            ? `.${fileMetadata?.allowedExtensions.join(',.')}`
            : ''
    }

    setInputsVisible(id: string, visible: boolean) {
        this.inputDependencies[id]?.forEach((inputId) => {
            if (inputId in this.visibleInputs) this.visibleInputs[inputId] = visible
        })
    }

    updateStepInput(inputItem: Input, value: any, index = 0) {
        if (this.inputData[inputItem.id] === undefined) {
            this.$set(this.inputData, inputItem.id, {})
        }

        if (inputItem.kind === 'radioGroup') {
            this.inputData[inputItem.id].selectedItems?.forEach((item: string) => {
                this.setInputsVisible(item, false)
            })
            this.inputData[inputItem.id].selectedItems = [value]
            this.setInputsVisible(value, true)
            this.setInputsVisible(inputItem.id, true)
        } else if (inputItem.kind === 'checkboxGroup') {
            this.inputData[inputItem.id].selectedItems?.forEach((item: string) =>
                this.setInputsVisible(item, false)
            )
            this.inputData[inputItem.id].selectedItems = value
            if (Array.isArray(value)) {
                this.setInputsVisible(inputItem.id, value.length > 0)
                value.forEach((item: string) => this.setInputsVisible(item, true))
            }
        }

        if (inputItem.kind === 'plainText') {
            this.inputData[inputItem.id].plainText = value
            if (!value) {
                delete this.inputData[inputItem.id].plainText
            }
            this.setInputsVisible(inputItem.id, Boolean(value))
        } else if (inputItem.kind === 'attachment') {
            if (!this.inputData[inputItem.id].file) {
                this.$set(this.inputData[inputItem.id], 'file', null)
            }
            this.inputData[inputItem.id].file = value
            this.setInputsVisible(inputItem.id, Boolean(value))
        } else if (inputItem.kind === 'multiAttachment') {
            if (!this.inputData[inputItem.id].files) {
                this.$set(this.inputData[inputItem.id], 'files', [])
            }
            if (value) {
                this.inputData[inputItem.id].files[index] = value
                ;(inputItem as DocumentInput).files[index] = { ...value, name: value.name }
            } else {
                this.inputData[inputItem.id].files = this.inputData[inputItem.id].files.map(
                    (file, i) => (i === index ? null : file)
                )
            }
            this.setInputsVisible(inputItem.id, Boolean(value))
        }

        this.updateResponse()
    }

    validateQuiz(stepIndex: number) {
        let currentStep = this.revisionData.data.steps[stepIndex - 1]

        if (!currentStep || currentStep._type === 'prepend-step' || !currentStep.quiz) {
            return true
        }

        let isValid = true

        for (let quizIndex = 0; quizIndex < currentStep.quiz.length; quizIndex++) {
            let quiz = currentStep.quiz[quizIndex]
            let quizKey = this.stepQuizKey(stepIndex - 1, quizIndex)

            if (quiz.kind === 'chooseOne') {
                isValid =
                    this.stepQuiz[quizKey] ===
                    quiz.answers.findIndex((answer: any) => answer.isCorrect)
            } else if (quiz.kind === 'chooseMany') {
                let selectedAnswers = this.stepQuiz[quizKey] || []
                isValid = quiz.answers.every(
                    (answer, index) => answer.isCorrect === selectedAnswers[index]
                )
            }

            this.$set(this.stepQuiz, this.stepQuizKey(stepIndex - 1, quizIndex, true), !isValid)

            if (!isValid) break
        }

        return isValid
    }

    validateStep(stepIndex = this.currentStepIndex) {
        let currentFormRef = this.$refs[`formRef${stepIndex}`]
        if (Array.isArray(currentFormRef)) {
            // @ts-ignore
            let isValid = currentFormRef[0].validate()
            if (!isValid) return false
        }

        return this.validateQuiz(stepIndex)
    }

    async updateResponse({ notifySaveStatus = true } = {}) {
        if (this.isPreview) return

        // clear step multi attachment [null] values
        for (let inputId in this.inputData) {
            if (this.inputData[inputId].files) {
                this.inputData[inputId].files = this.inputData[inputId].files.filter((file) => file)
            }
        }

        // delete populated input data if the input is not available in current form revision
        for (let inputId in this.inputData) {
            if (typeof this.visibleInputs[inputId] === 'undefined') {
                delete this.inputData[inputId]
            } else if (
                this.inputData[inputId] &&
                Object.keys(this.inputData[inputId]).length === 0
            ) {
                delete this.inputData[inputId]
            }
        }

        notifySaveStatus && this.notifySaveStatus('saving')
        return patchRequest(
            `${this.formBaseUrl}/formResponses/${this.formResponse.id}`,
            {},
            serialise('formResponse', {
                id: this.formResponse.id,
                inputData: Object.keys(this.inputData).length > 0 ? this.inputData : null,
            }).data
        )
            .catch(() => {
                notifySaveStatus && this.notifySaveStatus(null)
            })
            .then(() => notifySaveStatus && this.notifySaveStatus('saved'))
    }

    notifySaveStatus(status: string | null) {
        this.$emit('saveStatus', status)
    }

    async nextStep() {
        if (!this.validateStep()) return

        if (this.isLastStep) {
            await this.submitResponse()
        } else {
            this.currentStepIndex++
        }
    }

    updateStepQuiz(
        stepIndex: number,
        quiz: any,
        quizIndex: number,
        value: [number, boolean] | number
    ) {
        let quizKey = this.stepQuizKey(stepIndex, quizIndex)

        if (quiz.kind === 'chooseMany') {
            if (this.stepQuiz[quizKey] === undefined) {
                this.stepQuiz[quizKey] = new Array(quiz.answers.length).fill(false)
            }
            this.stepQuiz[quizKey][value[0]] = value[1]
        } else {
            this.stepQuiz[quizKey] = value
        }
    }

    @Debounce(1000)
    handlePlainTextChanged(inputItem: Input, value: string) {
        this.updateStepInput(inputItem, value)
    }

    async handleFileUpload(inputItem: DocumentInput, file: File, index = 0) {
        if (!file) {
            return this.updateStepInput(inputItem, null, index)
        }

        if (!(file instanceof File)) {
            return
        }

        try {
            let fileId
            this.isUploadingFile = true

            if (!this.isPreview) fileId = await this.postFile(file)

            this.updateStepInput(
                inputItem,
                {
                    id: fileId,
                    filename: file.name,
                    size: file.size,
                },
                index
            )
        } finally {
            this.isUploadingFile = false
        }
    }

    async submitResponse() {
        if (this.isPreview) return

        for (let stepIndex = 1; stepIndex <= this.revisionSteps.length; stepIndex++) {
            if (!this.validateStep(stepIndex)) {
                this.currentStepIndex = stepIndex
                return
            }
        }

        try {
            this.isSubmitting = true
            // update response again in case other update request is in progress (which might not have saved the required inputs, then lead to submission error)
            await this.updateResponse({ notifySaveStatus: false })
            await this.submit()
            this.$emit('submitted')
        } finally {
            this.isSubmitting = false
        }
    }

    async submit() {
        await postRequest(
            `${this.formBaseUrl}/formResponses/${this.formResponse.id}/submit`,
            {}
        ).catch(async (error) => {
            if (error.data.response.status === 412) {
                await this.submit()
            }
        })
    }

    restoreInputData() {
        for (let id in this.inputDependencies) {
            this.setInputsVisible(id, false)
        }

        for (let id in this.inputData) {
            let hasValue = false
            this.inputData[id].selectedItems?.forEach((item: string) => {
                if (item) {
                    hasValue = true
                    this.setInputsVisible(item, true)
                }
            })
            if (this.inputData[id].plainText) hasValue = true
            if (this.inputData[id].file) hasValue = true
            this.inputData[id].files?.forEach((file) => {
                if (file) hasValue = true
            })
            this.setInputsVisible(id, hasValue)
        }
    }

    startOver() {
        this.inputData = {}
        this.isResponseAvailable = false
        this.isLatestResponseAvailable = false
        this.initSteps()
        this.setInputDocuments()
        this.restoreInputData()
        this.updateResponse()
    }

    async viewLatestResponse() {
        this.$emit('viewResponse', this.latestResponse)
    }

    isNullOrUndefined(value: any) {
        return value === null || value === undefined
    }

    inputComparer(objValue, othValue) {
        if (this.isNullOrUndefined(objValue) && this.isNullOrUndefined(othValue)) {
            return true
        }

        if (
            this.isNullOrUndefined(objValue.scoreIfSelected) &&
            this.isNullOrUndefined(othValue.scoreIfSelected)
        ) {
            delete objValue.scoreIfSelected
            delete othValue.scoreIfSelected
        }
        if (
            this.isNullOrUndefined(objValue.scoreIfNotSelected) &&
            this.isNullOrUndefined(othValue.scoreIfNotSelected)
        ) {
            delete objValue.scoreIfNotSelected
            delete othValue.scoreIfNotSelected
        }
        if (objValue?._id || othValue?._id) {
            delete objValue._id
            delete othValue._id
        }
    }

    stepQuizKey(stepIndex: number, quizIndex: number, isError = false) {
        return `step${stepIndex}.quiz${quizIndex}` + (isError ? `-error` : '')
    }

    deleteStepInputData(id: string) {
        if (this.inputData[id]) {
            delete this.inputData[id]
        }

        // delete input dependencies data
        this.inputDependencies[id]?.forEach((inputId) => {
            this.deleteStepInputData(inputId)
        })
    }

    setInputDocuments() {
        this.revisionSteps.forEach((step) => {
            step.inputs?.forEach((input) => {
                if (input instanceof DocumentInput) {
                    input.setFiles(this.inputData[input.id])
                }
            })
        })
    }

    compareSteps() {
        this.revisionData.data.steps.forEach((step, stepIndex) => {
            let previousStep = this.latestResponse.revision.data.data.steps.find(
                (s) => s.title?.[this.language] === step.title?.[this.language]
            )

            if (!previousStep) {
                this.stepInputChanges[stepIndex] = 'Added'
                return
            }

            step.inputs?.forEach((input) => {
                let previousInput = previousStep.inputs?.find((i) => i.id === input.id)
                let stepInputState: StepInputState = previousInput ? '' : 'Added'

                if (previousInput) {
                    let isInputUnchanged = isEqualWith(input, previousInput, this.inputComparer)
                    if (!isInputUnchanged) stepInputState = 'Updated'
                }
                if (stepInputState) this.stepInputChanges[stepIndex] = 'Updated'
                this.stepInputChanges[input.id] = stepInputState

                // if input has been changed, remove the previous input data
                if (stepInputState !== '') {
                    this.deleteStepInputData(input.id)
                    input.items?.forEach((item) => this.deleteStepInputData(item.id))
                }
            })

            step.quiz?.forEach((quiz, quizIndex) => {
                let previousQuiz = previousStep.quiz?.[quizIndex]
                let stepInputState: StepInputState = previousQuiz ? '' : 'Added'
                let quizKey = this.stepQuizKey(stepIndex, quizIndex)

                if (previousQuiz) {
                    let isQuizUnchanged = isEqualWith(quiz, previousQuiz, this.inputComparer)
                    if (!isQuizUnchanged) stepInputState = 'Updated'
                }
                if (stepInputState) this.stepInputChanges[stepIndex] = 'Updated'
                this.stepInputChanges[quizKey] = stepInputState
            })
        })
    }

    populateForm() {
        this.inputData = this.latestResponse.inputData
        this.isLatestResponseAvailable = false
        this.isResponseAvailable = false

        this.compareSteps()
        this.setInputDocuments()
        this.restoreInputData()
        this.updateResponse()
    }

    initSteps() {
        this.revisionSteps = []
        this.visibleInputs = {}
        this.inputDependencies = {}

        this.revisionData?.data?.steps?.forEach((step) => {
            this.revisionSteps.push(new Step(this.language, step, this.inputData))
            step.inputs?.forEach((input) => {
                this.$set(this.visibleInputs, input.id, input.dependsOn ? false : true)
                if (!this.inputDependencies[input.id])
                    this.inputDependencies[input.id] = input.items?.map((item) => item.id) || []
                if (input.dependsOn) {
                    if (!this.inputDependencies[input.dependsOn])
                        this.inputDependencies[input.dependsOn] = []
                    this.inputDependencies[input.dependsOn].push(input.id)
                }
            })
        })

        if (this.$scopedSlots['prepend-step-title']) {
            const lastStep = new Step(this.language)
            lastStep._type = 'prepend-step'
            this.revisionSteps.push(lastStep)
        }
    }

    created() {
        this.initSteps()
        if (this.isResponseAvailable) {
            this.updateResponse({ notifySaveStatus: false })
        }
    }

    mounted() {
        this.restoreInputData()

        if (!this.$refs.formResponseEl) return

        // Options for the observer (which mutations to observe)
        const config = { childList: true, subtree: true }

        // Callback function to execute when mutations are observed
        const callback = (mutationList) => {
            for (const mutation of mutationList) {
                if (mutation.type !== 'childList') continue

                mutation.removedNodes.forEach((node: HTMLElement) => {
                    if (!node.dataset?.id) return
                    delete this.inputData[node.dataset.id]
                    this.setInputsVisible(node.dataset.id, false)
                })

                mutation.addedNodes.forEach((node: HTMLElement) => {
                    if (!node.dataset?.id || !this.inputData[node.dataset.id]) return
                    this.setInputsVisible(node.dataset.id, true)
                })
            }
        }

        // Create an observer instance linked to the callback function
        this.domObserver = new MutationObserver(callback)

        // // Start observing the target node for configured mutations
        this.domObserver.observe(this.$refs.formResponseEl as Node, config)
    }

    beforeDestroy() {
        this.domObserver?.disconnect()
    }
}
